import { buildRoute } from '../../config/routes';
import { orderViewQuery } from './order-views/orderViews';

/** The fields of an orders-list row that opening its assets workspace needs. */
export interface OrderRowForAssets {
  /** Both typed as the raw order row carries them; a row without them still
   *  navigates (to `/undefined/assets`, prior behaviour) rather than throwing. */
  propertyId: string | null | undefined;
  orderId: string | null | undefined;
  /** The order's own photoset link (order-service populates it, DV-1867). */
  photosetUUID?: string | null;
  formattedAddress?: string;
  property?: {
    propertyType?: string;
    propertySource?: string | null;
    hasPhotoset?: boolean | null;
  } | null;
}

export interface OrderRowAssetsNav {
  path: string;
  query: Record<string, string>;
  state: {
    propertyAddress?: string;
    propertyType?: string;
    propertySource?: string | null;
    hasPhotoset?: boolean | null;
    orderUUID?: string;
    orderPhotosetUUID?: string;
  };
}

/**
 * DV-3521: how an orders-list row opens the assets workspace. One rule for
 * both of the list's Manage Assets entries (the Assets cell and the row's
 * Quick Actions), so neither can drift from the other:
 *
 *  - the route names the order's own photoset when it has one, else the
 *    property's assets (the workspace then lands on the property's newest set,
 *    prior behaviour for an unlinked order);
 *  - the query carries `orderUUID` (uploads are that order's, not the
 *    property's latest order's) and `orderPhotosetUUID` (the order's own link,
 *    so a linked add-on is not warned that the set belongs to another order),
 *    so a reload, a new tab or a copied link keep them; plus the list's preset
 *    view, so the workspace's Back returns to it (DV-3455);
 *  - router state seeds the header and the first grid paint before the
 *    property fetch lands.
 *
 * The Global Orders entry builds the same query through
 * `buildRoute.globalOrderAssets`.
 */
export const orderRowAssetsNav = (
  row: OrderRowForAssets,
  activeView: string | null | undefined,
  extraParams: Record<string, string> = {}
): OrderRowAssetsNav => {
  const photosetUUID = row.photosetUUID || undefined;
  return {
    path: buildRoute.propertyAssets(String(row.propertyId), { photosetUUID }),
    query: {
      source: 'order',
      ...extraParams,
      ...orderViewQuery(activeView),
      ...(row.orderId ? { orderUUID: row.orderId } : {}),
      ...(photosetUUID ? { orderPhotosetUUID: photosetUUID } : {})
    },
    state: {
      propertyAddress: row.formattedAddress,
      propertyType: row.property?.propertyType,
      propertySource: row.property?.propertySource,
      hasPhotoset: row.property?.hasPhotoset,
      orderUUID: row.orderId || undefined,
      orderPhotosetUUID: photosetUUID
    }
  };
};
